import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { FirestoreContextService } from './firestore-context.service';
import { BehaviorSubject } from 'rxjs';
import { MediaBannerFields } from '../models/reel.model';

export interface BannerPromotion extends MediaBannerFields {
  id: string;
  active: boolean;
  businessId: number;
  imageUrl: string;
  type: 'event' | 'product' | 'navigationLink' | 'media';
  eventId?: string;
  productId?: number;
  navigationUrl?: string;
}

// Simplified interface for storage (non-media banners only — see setCachedBanner).
interface StoredBanner {
  /**
   * Kept purely so `banner_shown` / `banner_open` stay attributable on a cache hit.
   *
   * This used to be dropped and rehydrated as `''`, which meant the first page load reported the
   * real banner id and every subsequent one in the session reported an empty string — so the
   * dashboard's per-banner engagement ranking under-counted whichever banners people saw most.
   */
  id: string;
  imageUrl: string;
  type: 'event' | 'product' | 'navigationLink' | 'media';
  businessId: number;
  eventId?: string;
  productId?: number;
  navigationUrl?: string;
}

@Injectable({
  providedIn: 'root'
})
export class BannerService {
  private currentBanner = new BehaviorSubject<BannerPromotion | null>(null);
  currentBanner$ = this.currentBanner.asObservable();
  private readonly STORAGE_KEY = 'active_banner_promotion';

  constructor(private afs: AngularFirestore, private fsContext: FirestoreContextService) {}

  private getCachedBanner(businessId: number): BannerPromotion | null {
    const cachedData = sessionStorage.getItem(this.STORAGE_KEY);
    if (cachedData) {
      const stored = JSON.parse(cachedData) as StoredBanner & Partial<BannerPromotion>;
      // Only return cached banner if it's for the same business
      if (stored.businessId === businessId) {
        // Media banners were cached whole (see setCachedBanner) so the reel payload survives —
        // return the full document as-is (its real id is preserved for analytics parity).
        if (stored.type === 'media') {
          return stored as BannerPromotion;
        }
        // Convert stored banner back to full banner promotion
        return {
          id: stored.id ?? '', // `?? ''` covers a cache entry written by an older build
          active: true, // If it's in cache, it was active
          businessId: stored.businessId,
          imageUrl: stored.imageUrl,
          type: stored.type,
          eventId: stored.eventId,
          productId: stored.productId,
          navigationUrl: stored.navigationUrl
        };
      }
    }
    return null;
  }

  private setCachedBanner(banner: BannerPromotion | null) {
    if (banner) {
      if (banner.type === 'media') {
        // Media banners carry the full reel payload (mediaItems, localized maps, end-card
        // fields) that the reduced StoredBanner shape would strip — cache the whole document so
        // a returning visitor still opens the complete reel from cache instead of an inert banner.
        sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(banner));
      } else {
        // Store all necessary fields
        const storedBanner: StoredBanner = {
          id: banner.id,
          imageUrl: banner.imageUrl,
          type: banner.type,
          businessId: banner.businessId,
          eventId: banner.eventId,
          productId: banner.productId,
          navigationUrl: banner.navigationUrl
        };
        sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(storedBanner));
      }
    } else {
      sessionStorage.removeItem(this.STORAGE_KEY);
    }
  }

  async fetchActiveBanner(businessId: number, forceRefresh = false): Promise<BannerPromotion | null> {
    // First check cache — unless we're forcing a fresh check (data refresh). The
    // sessionStorage cache survives reloads, so without this a new / updated /
    // removed banner on the backend would stay masked by the cached one.
    if (!forceRefresh) {
      const cachedBanner = this.getCachedBanner(businessId);
      if (cachedBanner) {
        this.currentBanner.next(cachedBanner);
        return cachedBanner;
      }
    }

    try {
      // COMPAT API
      const snapshot = await this.fsContext.run(() =>
        this.afs.collection<BannerPromotion>('banner-promotions', ref =>
          ref.where('businessId', '==', businessId)
             .where('active', '==', true)
        ).get()
      ).toPromise();

      if (snapshot && !snapshot.empty) {
        const doc = snapshot.docs[0];
        const bannerData = { ...doc.data() } as BannerPromotion;
        bannerData.id = doc.id; // Assign id separately to avoid overwriting
        // Cache only essential fields
        this.setCachedBanner(bannerData);
        this.currentBanner.next(bannerData);
        return bannerData;
      }
      
      this.setCachedBanner(null);
      this.currentBanner.next(null);
      return null;
    } catch (error) {
      console.error('Error fetching banner promotion:', error);
      this.setCachedBanner(null);
      this.currentBanner.next(null);
      return null;
    }
  }

  clearBannerCache() {
    sessionStorage.removeItem(this.STORAGE_KEY);
    this.currentBanner.next(null);
  }
} 